/**
 * Collision prompt rules (UI/UX fix round A — reviews A3 / U4 / A18): what a prompt offers, the name "Keep both"
 * gives, and whether a remembered "do the same for the other conflicts" choice answers a prompt by itself. Pure — the
 * main process owns the prompts (`collisionPrompts.ts`), one per job at a time.
 */
import type { TransferCollisionDecision, TransferJob } from '../../../types/azureStorageTransfers';

export type CollisionAction = TransferCollisionDecision['action'];
export type PendingCollision = NonNullable<TransferJob['pendingCollision']>;

/** An in-job duplicate never overwrites a sibling (review D D5); a run-time prompt names the actions it offers (S7 §3.6). */
export function collisionOffers(pending: PendingCollision, action: CollisionAction): boolean {
  if (action === 'overwrite' && pending.reason === 'duplicate-in-job') return false;
  return !pending.actions || pending.actions.includes(action);
}

/**
 * The first number Keep both tries: a file-share upload names its copy `name (1).ext` (`files/fileUploadPlanning.ts`,
 * `fileUploadPublish.ts`), a blob upload and a local download `name (2).ext` (`uploadNaming.ts`, `pathSafety.ts`).
 */
export function keepBothFirstNumber(job: Pick<TransferJob, 'kind' | 'destination'>): number {
  const destination = job.destination as TransferJob['destination'] | undefined;
  const fileShare = destination?.kind === 'remote' && (destination.resource.kind === 'share' || destination.resource.kind === 'directory');
  return job.kind === 'upload' && fileShare ? 1 : 2;
}

/** "report (2).csv" — the name Keep both gives when it is free; main takes the next free number (`name (n).ext`). */
export function keepBothExample(destinationLabel: string, firstNumber = 2): string {
  const slash = destinationLabel.lastIndexOf('/');
  const name = destinationLabel.slice(slash + 1) || destinationLabel;
  const dot = name.lastIndexOf('.');
  return dot > 0 ? `${name.slice(0, dot)} (${firstNumber})${name.slice(dot)}` : `${name} (${firstNumber})`;
}

/** The remembered choice answers this prompt only when the prompt offers it; otherwise the user is asked. */
export function autoCollisionAction(pending: PendingCollision | undefined, rule: CollisionAction | undefined): CollisionAction | undefined {
  return pending && rule && collisionOffers(pending, rule) ? rule : undefined;
}

/** What Cancel says: the job's own kind, never a generic "transfer" when it is known. */
export function collisionCancelLabel(kind: TransferJob['kind']): string {
  return kind === 'upload' ? 'Cancel upload' : kind === 'download' ? 'Cancel download' : 'Cancel transfer';
}
